import React, { useEffect, useMemo, useState } from "react";
import QuestionBlock from "./QuestionBlock";
import { buildVisibleQuestions, convertLetterCase } from "../../../../utils/Helper";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetAssignedReferenceQuestionsQuery,
  useSubmitReferenceWithAnswersMutation
} from "../../../../store/Company/companyApiSlice";
import { Breadcrumb, Modal } from "../../../common";
import SubmissionSuccess from "../../../common/SubmissionSuccess";

const Questionnaire = () => {
  const { token } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const locationData = location.state;

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [questionsData, setQuestionsData] = useState({
    questions_set: [],
    related_questions: []
  });
  const { questions_set, related_questions } = questionsData;

  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [apiError, setApiError] = useState("");

  const {
    data: getAssignedReferenceQuestions,
    isLoading: isQuestionsLoading
  } = useGetAssignedReferenceQuestionsQuery({
    token,
    id: locationData?.id
  });

  const [
    submitReferenceWithAnswers,
    { isLoading: isSubmitting }
  ] = useSubmitReferenceWithAnswersMutation();

  /* ---------------- CONSENT STEP ---------------- */

  const appendConsentStep = (data) => {
    const CONSENT_STEP = {
      question_uid: "__CONSENT__",
      question_text: "Consent / Disclaimer",
      question_type: "consent",
      is_required: true,
      is_active: true
    };

    return {
      ...data,
      questions_set: [...data.questions_set, CONSENT_STEP]
    };
  };

  /* ---------------- LOAD QUESTIONS ---------------- */

  useEffect(() => {
    if (getAssignedReferenceQuestions?.questions) {
      const modifiedData = appendConsentStep(
        getAssignedReferenceQuestions.questions
      );
      setQuestionsData(modifiedData);
    }
  }, [getAssignedReferenceQuestions]);

  /* ---------------- RELATED QUESTION MAP ---------------- */

  const relatedMap = useMemo(() => {
    const map = {};
    related_questions?.forEach((q) => {
      map[q.question_uid] = q;
    });
    return map;
  }, [related_questions]);

  /* ---------------- VISIBLE QUESTIONS ---------------- */

  const visibleQuestions = useMemo(
    () => buildVisibleQuestions(questions_set, relatedMap, answers),
    [questions_set, relatedMap, answers]
  );

  /* ---------------- HANDLERS ---------------- */

  const handleNext = () => {
    if (activeIndex < visibleQuestions.length - 1) {
      setActiveIndex((prev) => prev + 1);
    }
  };

  const handleChange = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const handleSubmit = async () => {
    setApiError("");
    const newErrors = {};

    const FIRST_QUESTION_UID = visibleQuestions[0]?.question_uid;
    const isFirstAnswerNo = answers[FIRST_QUESTION_UID] === "No";

    /* -------- VALIDATION (SKIP IF FIRST ANSWER = NO) -------- */

    if (!isFirstAnswerNo) {
      visibleQuestions.forEach((q) => {
        if (
          q.is_required &&
          q.question_uid !== "__CONSENT__" &&
          !answers[q.question_uid]
        ) {
          newErrors[q.question_uid] = "This field is required";
        }
      });

      if (!answers["__CONSENT__"]) {
        newErrors["__CONSENT__"] = "Please confirm the consent to submit.";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        const firstErrorKey = Object.keys(newErrors)[0];
        document
          .getElementById(firstErrorKey)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }

    setErrors({});

    /* ---------------- BUILD PAYLOAD ---------------- */

    const responses = Object.entries(answers)
      .filter(([key]) => key !== "__CONSENT__")
      .reduce((acc, [question_uid, answer]) => {
        acc[question_uid] = answer;
        return acc;
      }, {});

    try {
      await submitReferenceWithAnswers({
        token,
        id: locationData?.id,
        data: responses
      }).unwrap();

      setShowSuccessModal(true);
    } catch (err) {
      setApiError(
        err?.data?.message ||
          err?.error ||
          "Something went wrong. Please try again."
      );
    }
  };

  const modalClose = () => {
    setShowSuccessModal(false);
    navigate(-1);
  };

  /* ---------------- RENDER ---------------- */

  return (
    <>
      <div className="max-w-4xl mx-auto relative text-left">
        {/* COMPANY NAME */}
        <div className="rounded-[14px] py-[24px] px-4 text-[14px] bg-white border border-[#D0D5DD] mb-[18px]">
          {convertLetterCase(locationData?.companyName, "titlecase")}
        </div>

        {/* API ERROR */}
        {apiError && (
          <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-300 text-red-700 text-sm">
            {apiError}
          </div>
        )}

        {/* QUESTIONS */}
        <div className="bg-[#F2F4F7] p-3 rounded-xl border border-[#D0D5DD] relative">
          {isSubmitting && (
            <div className="absolute inset-0 bg-white/70 z-20 flex items-center justify-center rounded-xl">
              <p className="text-sm text-gray-600">Submitting...</p>
            </div>
          )}

          {isQuestionsLoading ? (
            <p className="text-center text-sm text-gray-500">
              Loading questions...
            </p>
          ) : (
            visibleQuestions.map((question, index) => (
              <QuestionBlock
                key={question.question_uid}
                question={question}
                value={answers[question.question_uid]}
                error={errors[question.question_uid]}
                isActive={index === activeIndex}
                isAnswered={index < activeIndex}
                isLocked={index > activeIndex || isSubmitting}
                isFirstQuestion={index === 0}
                onChange={handleChange}
                onNext={handleNext}
                onSubmit={handleSubmit}
              />
            ))
          )}
        </div>
      </div>

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <Modal modalHandle={modalClose}>
          <SubmissionSuccess
            companyName={convertLetterCase(
              locationData?.companyName,
              "titlecase"
            )}
          />
        </Modal>
      )}
    </>
  );
};

export default Questionnaire;

